import { useEventListener } from "@vueuse/core"

type AssistantSyncStorageEntryType = "chat" | "draft" | "flow"

const ASSISTANT_SYNC_STORAGE_KEY_PREFIX = "assistant-flow"
const ASSISTANT_SYNC_STORAGE_REGISTRY_KEY = `${ASSISTANT_SYNC_STORAGE_KEY_PREFIX}:registry`

let stopAssistantSyncStorageCleanupListener: null | (() => void) = null

function readRegisteredAssistantFlowIds(): number[] {
  const rawRegisteredAssistantFlowIds = localStorage.getItem(ASSISTANT_SYNC_STORAGE_REGISTRY_KEY)
  if (!rawRegisteredAssistantFlowIds) {
    return []
  }

  const parsedRegisteredAssistantFlowIds = JSON.parse(rawRegisteredAssistantFlowIds)
  if (!Array.isArray(parsedRegisteredAssistantFlowIds)) {
    throw new Error("Assistant sync storage registry must contain an array of flow ids.")
  }

  return parsedRegisteredAssistantFlowIds as number[]
}

function writeRegisteredAssistantFlowIds(registeredAssistantFlowIds: number[]) {
  if (!registeredAssistantFlowIds.length) {
    localStorage.removeItem(ASSISTANT_SYNC_STORAGE_REGISTRY_KEY)
    return
  }

  localStorage.setItem(ASSISTANT_SYNC_STORAGE_REGISTRY_KEY, JSON.stringify(registeredAssistantFlowIds))
}

function installAssistantSyncStorageCleanupListener() {
  if (stopAssistantSyncStorageCleanupListener) {
    return
  }

  stopAssistantSyncStorageCleanupListener = useEventListener(window, "pagehide", clearRegisteredAssistantSyncStorage)
}

export function createAssistantSyncStorageKey(aiFlowId: number, entryType: AssistantSyncStorageEntryType): string {
  return `${ASSISTANT_SYNC_STORAGE_KEY_PREFIX}:${aiFlowId}:${entryType}`
}

export function registerAssistantSyncStorageFlow(aiFlowId: number) {
  if (!Number.isInteger(aiFlowId) || aiFlowId <= 0) {
    return
  }

  installAssistantSyncStorageCleanupListener()

  const registeredAssistantFlowIds = readRegisteredAssistantFlowIds()
  if (registeredAssistantFlowIds.includes(aiFlowId)) {
    return
  }

  writeRegisteredAssistantFlowIds([...registeredAssistantFlowIds, aiFlowId])
}

export function clearAssistantSyncStorageForFlow(aiFlowId: number) {
  localStorage.removeItem(createAssistantSyncStorageKey(aiFlowId, "flow"))
  localStorage.removeItem(createAssistantSyncStorageKey(aiFlowId, "chat"))
  localStorage.removeItem(createAssistantSyncStorageKey(aiFlowId, "draft"))
}

export function clearRegisteredAssistantSyncStorage() {
  const registeredAssistantFlowIds = readRegisteredAssistantFlowIds()
  registeredAssistantFlowIds.forEach((registeredAssistantFlowId) => {
    clearAssistantSyncStorageForFlow(registeredAssistantFlowId)
  })
  localStorage.removeItem(ASSISTANT_SYNC_STORAGE_REGISTRY_KEY)
}

export function getAssistantSyncStorageRegistryKey(): string {
  return ASSISTANT_SYNC_STORAGE_REGISTRY_KEY
}
